import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import { localViews } from 'modules/Auth/AuthProvider/views';
import { forceLogoutRedirect } from 'modules/Auth/AuthProvider/forceLogout';
import { Auth0StaffSessionEndedError } from 'modules/Auth/auth0/staffImpersonation';
import { auth0AuthorizationHeader, auth0StaffImpersonationHeader } from 'modules/Auth/auth0/tokenBridge';
import { getAmplitudeSessionReplayId } from 'modules/common/utils/amplitudeSessionReplay';

type Headers = {
    [name: string]: string | number;
};

export const STAFF_TOKEN_HEADER = 'X-Staff-Token-While-Impersonate';

// A Django staffer's impersonation header, which needs no SDK.
function djangoStaffHeaders(): Headers {
    const staffTokenWhileImpersonate = localViews.staffDetails()?.token;

    return staffTokenWhileImpersonate?.access
        ? { [STAFF_TOKEN_HEADER]: `JWT ${staffTokenWhileImpersonate.access}` }
        : {};
}

export default function getCollectiveHeaders() {
    const localToken = localViews.token();
    const token = localToken?.access;
    const ephemeralToken = localToken?.ephemeral_token;
    const headers: Headers = {};

    if (token) {
        headers.Authorization = `JWT ${token}`;
    } else if (ephemeralToken) {
        headers.Authorization = `Token ${ephemeralToken}`;
    }

    Object.assign(headers, djangoStaffHeaders());

    const sessionReplayId = getAmplitudeSessionReplayId();
    if (sessionReplayId) {
        headers['X-Amplitude-Session-Replay-Id'] = sessionReplayId;
    }

    return headers;
}

/**
 * The staff header for an impersonation started from an Auth0 session, which only the SDK can
 * fill; getCollectiveHeaders sends a Django staffer's. Rejects rather than leave it off, and
 * signs out once the staffer's session has ended.
 */
export async function getAuth0StaffHeaders(): Promise<Headers> {
    try {
        const staffHeader = await auth0StaffImpersonationHeader();

        return staffHeader ? { [STAFF_TOKEN_HEADER]: staffHeader } : {};
    } catch (error) {
        if (error instanceof Auth0StaffSessionEndedError) {
            forceLogoutRedirect();
        }
        throw error;
    }
}

/** The impersonation header alone, for a Django or an Auth0 staffer, on getAuth0StaffHeaders' terms. */
export async function getStaffHeaders(): Promise<Headers> {
    return { ...djangoStaffHeaders(), ...(await getAuth0StaffHeaders()) };
}

/** getCollectiveHeaders, carrying the Auth0 bearer when tokenBridge chooses it over a Django credential. */
export async function getCollectiveHeadersWithAuth0() {
    const headers = { ...getCollectiveHeaders(), ...(await getAuth0StaffHeaders()) };
    const auth0Authorization = await auth0AuthorizationHeader();
    if (auth0Authorization) {
        headers.Authorization = auth0Authorization;
    }

    return headers;
}

// Fails the upload as a network error instead of sending it.
function refuseToSend(xhr: XMLHttpRequest) {
    // eslint-disable-next-line no-param-reassign
    xhr.send = () => {
        xhr.dispatchEvent(new ProgressEvent('error'));
    };
}

/**
 * Adds the Auth0 bearer to an upload's request when tokenBridge chooses it, and the Auth0 staff
 * header while impersonating. Never throws: Uppy awaits its onBeforeRequest inside a promise
 * executor, so a throw would leave the upload hanging. Without the bearer the upload fails as an
 * ordinary upload error instead; without the staff header it is never sent.
 */
export async function applyAuth0Authorization(xhr: XMLHttpRequest): Promise<void> {
    try {
        Object.entries(await getAuth0StaffHeaders()).forEach(([name, value]) => {
            xhr.setRequestHeader(name, String(value));
        });
    } catch (error) {
        sendDatadogRumError('Upload refused without the staff header', error);
        refuseToSend(xhr);
        return;
    }

    try {
        const auth0Authorization = await auth0AuthorizationHeader();
        if (auth0Authorization) {
            xhr.setRequestHeader('Authorization', auth0Authorization);
        }
    } catch (error) {
        sendDatadogRumError('Upload sent without the Auth0 bearer', error);
    }
}
